import {useState} from "react";
import {addDoc, collection} from "firebase/firestore";
import {db} from "../firebase";
import {useAuth} from "../auth/AuthContext";
import {useNavigate, useLocation} from "react-router-dom";

export default function CreateCourse() {
    const {user} = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const initialTitle = (location.state as any)?.title || "";
    const [title, setTitle] = useState(initialTitle);
    const [grade, setGrade] = useState("");
    const [description, setDescription] = useState("");
    const [goals, setGoals] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const parsedGoals = goals
        .split("\n")
        .map(g => g.trim())
        .filter(Boolean);

    const validate = () => {
        if (!user) return "Du musst eingeloggt sein.";
        if (!title.trim()) return "Bitte gib einen Kursnamen ein.";
        if (!grade.trim()) return "Bitte gib eine Klassenstufe an.";
        if (!description.trim()) return "Bitte gib eine Kurzbeschreibung ein.";
        if (description.trim().length < 20) return "Die Beschreibung sollte mindestens 20 Zeichen haben.";
        return null;
    };

    const createCourse = async () => {
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }

        setError(null);
        setLoading(true);

        try {
            const docRef = await addDoc(collection(db, "courses"), {
                title: title.trim(),
                grade: grade.trim(),
                description: description.trim(),
                learningGoals: parsedGoals,
                teacherId: user!.uid,
                createdAt: new Date(),
                status: "aktiv",
                active: true
            });

            navigate(`/courses/${docRef.id}`);
        } catch (err: any) {
            setError("Kurs konnte nicht erstellt werden. Bitte versuche es erneut.");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate("/courses");
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-gray-50 to-white text-gray-800 relative overflow-hidden">
            <div className="w-full max-w-2xl bg-white/90 backdrop-blur-sm rounded-3xl p-8 relative z-10 shadow-xl border border-gray-200 animate-float-in">
                <header className="text-center mb-8">
                    <h1 className="text-3xl lg:text-4xl font-bold mb-3 text-gray-800">Neuen Kurs Erstellen</h1>
                    <p className="text-gray-500">Gestalte deinen perfekten Kurs</p>
                </header>

                <div className="space-y-6">
                    {error && (
                        <div className="p-4 rounded-2xl bg-red-50 border border-red-200 text-red-600 flex items-center gap-3">
                            <span className="text-xl">⚠️</span>
                            <span>{error}</span>
                        </div>
                    )}

                    <div className="space-y-2">
                        <label className="text-gray-700 font-medium flex items-center gap-2">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            Kursname
                        </label>
                        <input
                            className="w-full px-5 py-4 rounded-2xl border border-gray-300 bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 transition-all"
                            type="text"
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            placeholder="Gib deinem Kurs einen inspirierenden Namen..."
                            maxLength={100}
                        />
                        <div className="text-gray-400 text-sm">{title.length}/100 Zeichen</div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-gray-700 font-medium flex items-center gap-2">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            Klassenstufe
                        </label>
                        <input
                            className="w-full px-5 py-4 rounded-2xl border border-gray-300 bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 transition-all"
                            type="text"
                            placeholder="z.B. 7. Klasse, Oberstufe, Anfänger"
                            value={grade}
                            onChange={e => setGrade(e.target.value)}
                            maxLength={50}
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-gray-700 font-medium flex items-center gap-2">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            Kurzbeschreibung
                        </label>
                        <textarea
                            className="w-full px-5 py-4 rounded-2xl border border-gray-300 bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 transition-all min-h-[140px] resize-vertical"
                            rows={4}
                            placeholder="Beschreibe den Kursinhalt, Ziele und was die Teilnehmer erwartet..."
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            maxLength={500}
                        />
                        <div className="text-gray-400 text-sm">
                            {description.length}/500 Zeichen • Mindestens 20 Zeichen empfohlen
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-gray-700 font-medium flex items-center gap-2">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            Lernziele (optional)
                        </label>
                        <textarea
                            className="w-full px-5 py-4 rounded-2xl border border-gray-300 bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 transition-all min-h-[140px] resize-vertical"
                            rows={4}
                            placeholder="Gib pro Zeile ein Lernziel ein...&#10;• Grundlagen verstehen&#10;• Praxisübungen meistern&#10;• Abschlussprojekt erstellen"
                            value={goals}
                            onChange={e => setGoals(e.target.value)}
                        />
                        {parsedGoals.length > 0 && (
                            <div className="space-y-2">
                                <div className="text-gray-500 text-sm">
                                    {parsedGoals.length} Lernziel{parsedGoals.length !== 1 ? 'e' : ''} definiert
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {parsedGoals.map((goal, i) => (
                                        <span key={i} className="px-4 py-2 rounded-full bg-green-50 border border-green-200 text-green-700 text-sm animate-slide-up">
                                            ✓ {goal}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex gap-4 pt-4">
                        <button
                            className="flex-1 px-6 py-4 rounded-2xl border border-gray-300 bg-white text-gray-700 hover:text-gray-800 hover:border-green-500 hover:bg-gray-50 transition-all duration-300 font-semibold disabled:opacity-50"
                            onClick={handleCancel}
                            disabled={loading}
                        >
                            Zurück
                        </button>
                        <button
                            className="flex-1 px-6 py-4 rounded-2xl bg-gradient-to-br from-green-500 to-green-600 text-white font-semibold hover:shadow-xl hover:shadow-green-500/40 hover:-translate-y-1 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            disabled={loading || !title.trim() || !grade.trim() || !description.trim()}
                            onClick={createCourse}
                        >
                            {loading ? (
                                <>
                                    <span className="inline-block w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                    Erstelle...
                                </>
                            ) : (
                                <>
                                    <span>Kurs Erstellen</span>
                                    <span className="text-xl">🚀</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {loading && (
                    <div className="absolute inset-0 bg-white/95 backdrop-blur-sm rounded-3xl flex flex-col items-center justify-center">
                        <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-full animate-pulse relative">
                            <div className="absolute -inset-2 bg-green-500/10 rounded-full blur-md"></div>
                        </div>
                        <div className="mt-4 text-green-600 font-medium tracking-wider">KURS WIRD ERSTELLT</div>
                    </div>
                )}
            </div>
        </div>
    );
}